import { Box, styled } from "@mui/material";
import { PointerEvent as ReactPointerEvent, useCallback, useRef } from "react";

import { type IdentifiedWindowEntry } from "contexts/windows";

import { WindowMinimizeContext } from "../windowMinimizeContext";
import { WindowDockedProvider } from "../windowRenderMode";
import { DOCK_MIN_HEIGHT, DOCK_RESIZE_HANDLE_HEIGHT } from "./constants";

const Slot = styled(Box)`
    pointer-events: auto;
    position: relative;
    background: #fff;
    border-radius: 8px;
    box-shadow: 2px 0 6px 0 rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    flex-shrink: 0;
`;

const ResizeHandle = styled("div")`
    position: absolute;
    left: 8px;
    right: 8px;
    bottom: 0;
    height: ${DOCK_RESIZE_HANDLE_HEIGHT}px;
    cursor: ns-resize;
    z-index: 2;
    touch-action: none;
`;

export interface DockedWindowProps {
    entry: IdentifiedWindowEntry;
    height: number;
    isLast: boolean;
    onClose: () => void;
    onToggleMinimize: () => void;
    onSetHeight: (height: number) => void;
}

/**
 * One slot of the desktop dock. Renders the registered component via
 * `WindowDockedProvider` (chrome + scrollable body, no Rnd/Panel) and wires
 * the chrome's minimize control to dock dispatches via `WindowMinimizeContext`.
 */
export function DockedWindow({ entry, height, isLast, onClose, onToggleMinimize, onSetHeight }: DockedWindowProps) {
    const Component = entry.config.component;
    const isMinimized = entry.state.minimized;
    const slotRef = useRef<HTMLDivElement | null>(null);
    // Cancels an in-flight drag if a second pointerdown arrives.
    const dragCleanupRef = useRef<(() => void) | null>(null);

    const handleResizePointerDown = useCallback(
        (e: ReactPointerEvent<HTMLDivElement>) => {
            if (isMinimized || !slotRef.current) {
                return;
            }
            dragCleanupRef.current?.();

            const startY = e.clientY;
            const startH = slotRef.current.getBoundingClientRect().height;
            const target = e.currentTarget;
            const pointerId = e.pointerId;
            target.setPointerCapture(pointerId);

            const onMove = (ev: PointerEvent) => {
                const delta = ev.clientY - startY;
                const next = Math.max(DOCK_MIN_HEIGHT, startH + delta);
                onSetHeight(next);
            };
            const cleanup = () => {
                target.removeEventListener("pointermove", onMove);
                target.removeEventListener("pointerup", onUp);
                target.removeEventListener("pointercancel", onUp);
                if (target.hasPointerCapture(pointerId)) {
                    target.releasePointerCapture(pointerId);
                }
                if (dragCleanupRef.current === cleanup) {
                    dragCleanupRef.current = null;
                }
            };
            const onUp = () => cleanup();

            target.addEventListener("pointermove", onMove);
            target.addEventListener("pointerup", onUp);
            target.addEventListener("pointercancel", onUp);
            dragCleanupRef.current = cleanup;
        },
        [isMinimized, onSetHeight],
    );

    return (
        <Slot
            ref={slotRef}
            style={{ height }}
            data-testid={`docked-window-${entry.id}`}
            data-docked-window-id={entry.id}
        >
            <WindowMinimizeContext.Provider value={{ minimized: isMinimized, onToggle: onToggleMinimize }}>
                <WindowDockedProvider>
                    <Component onClose={onClose} />
                </WindowDockedProvider>
            </WindowMinimizeContext.Provider>
            {!isLast && !isMinimized && (
                <ResizeHandle onPointerDown={handleResizePointerDown} data-testid={`dock-resize-${entry.id}`} />
            )}
        </Slot>
    );
}
